import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { openCalendlyDemo } from "@/lib/analytics";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface NavLink {
  label: string;
  to: string;
}

const PLATFORM_LINKS: NavLink[] = [
  { label: "AI Email Prevention", to: "/technology/ai-email-prevention" },
  { label: "Native ERP Integration", to: "/technology/native-erp" },
  { label: "Multi-Platform", to: "/technology/multi-platform" },
];

// WebCenter modernization cluster — strategic priority for the Dec 2026 cycle.
// Curated — full 20-page library accessible from the hub + footer.
const SOLUTIONS_WEBCENTER: NavLink[] = [
  { label: "Oracle FMW 12c End of Support", to: "/solutions/oracle-fusion-middleware-12c-end-of-support" },
  { label: "Modernize Oracle WebCenter AP", to: "/solutions/modernize-oracle-webcenter" },
  { label: "WebCenter 14c Upgrade", to: "/solutions/webcenter-14c-upgrade" },
  { label: "WebCenter Forms Recognition Experts", to: "/solutions/webcenter-forms-recognition-experts" },
  { label: "WebCenter Modernization Services", to: "/solutions/webcenter-modernization-services" },
];

// Industry-specific Oracle solutions.
const SOLUTIONS_INDUSTRY: NavLink[] = [
  { label: "Healthcare on Oracle Fusion", to: "/solutions/healthcare-oracle-fusion" },
  { label: "Education on Oracle EBS", to: "/solutions/education-oracle-ebs" },
];

const INDUSTRY_LINKS: NavLink[] = [
  { label: "Healthcare", to: "/industries/healthcare" },
  { label: "Property Management", to: "/industries/property-management" },
  { label: "Manufacturing", to: "/industries/manufacturing" },
  { label: "Education", to: "/industries/education" },
];

const INTEGRATION_LINKS: NavLink[] = [
  { label: "Oracle (EBS + Fusion)", to: "/integrations/oracle" },
  { label: "NetSuite", to: "/integrations/netsuite" },
  { label: "SAP (ECC + S/4HANA)", to: "/integrations/sap" },
  { label: "Microsoft Dynamics 365", to: "/integrations/dynamics" },
  { label: "Yardi Voyager", to: "/integrations/yardi" },
];

// WebCenter customer free tools — the consulting funnel.
const RESOURCES_WEBCENTER: NavLink[] = [
  { label: "Oracle FMW Support Dates", to: "/resources/oracle-fusion-middleware-support-dates" },
  { label: "WebCenter Decision Guide", to: "/resources/webcenter-decision-guide" },
  { label: "WCC Modernization Inspector (Free)", to: "/resources/webcenter-content-inspector" },
  { label: "WCC Component Inventory Pro (Free)", to: "/resources/wcc-component-inventory" },
  { label: "WFR Template Analyzer (Free)", to: "/resources/wfr-template-analyzer" },
  { label: "WebCenter TCO Calculator (Free)", to: "/resources/webcenter-tco-calculator" },
  { label: "AP Maturity Benchmark (Free)", to: "/resources/ap-maturity-benchmark" },
];

// General company resources.
const RESOURCES_COMPANY: NavLink[] = [
  { label: "ROI Calculator", to: "/resources/roi-calculator" },
  { label: "Customers", to: "/customers" },
  { label: "Blog", to: "/blog" },
  { label: "Conferences", to: "/conferences" },
  { label: "Plans", to: "/plans" },
  { label: "About", to: "/about" },
];

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleSectionNavigation = (sectionId: string) => {
    navigate("/", { replace: true });
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const handleLogoClick = () => {
    navigate("/", { replace: true });
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  return (
    <nav className="bg-white/95 backdrop-blur-sm shadow-soft sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-4">
          <div className="flex items-center">
            <Link to="/" aria-label="Go to home page" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <img
                src="/ezcloud-logo-v2.png"
                alt="EZ Cloud — AP automation for Oracle"
                width={229}
                height={48}
                className="h-12 w-auto"
                loading="eager"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-5">
            {/* Platform */}
            <DropdownMenu>
              <DropdownMenuTrigger className="text-foreground hover:text-primary transition-smooth flex items-center gap-1">
                Platform <ChevronDown size={16} aria-hidden />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {PLATFORM_LINKS.map((l) => (
                  <DropdownMenuItem asChild key={l.to}>
                    <Link to={l.to}>{l.label}</Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <button onClick={() => handleSectionNavigation("how-it-works")} className="w-full text-left">
                    How it works
                  </button>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Solutions */}
            <DropdownMenu>
              <DropdownMenuTrigger className="text-foreground hover:text-primary transition-smooth flex items-center gap-1">
                Solutions <ChevronDown size={16} aria-hidden />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {SOLUTIONS_WEBCENTER.map((l) => (
                  <DropdownMenuItem asChild key={l.to}>
                    <Link to={l.to}>{l.label}</Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                {SOLUTIONS_INDUSTRY.map((l) => (
                  <DropdownMenuItem asChild key={l.to}>
                    <Link to={l.to}>{l.label}</Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/solutions/modernize-oracle-webcenter" className="font-medium">
                    See all WebCenter solutions →
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Industries */}
            <DropdownMenu>
              <DropdownMenuTrigger className="text-foreground hover:text-primary transition-smooth flex items-center gap-1">
                Industries <ChevronDown size={16} aria-hidden />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {INDUSTRY_LINKS.map((l) => (
                  <DropdownMenuItem asChild key={l.to}>
                    <Link to={l.to}>{l.label}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Integrations */}
            <DropdownMenu>
              <DropdownMenuTrigger className="text-foreground hover:text-primary transition-smooth flex items-center gap-1">
                Integrations <ChevronDown size={16} aria-hidden />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {INTEGRATION_LINKS.map((l) => (
                  <DropdownMenuItem asChild key={l.to}>
                    <Link to={l.to}>{l.label}</Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/integrations">See all integrations</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Resources */}
            <DropdownMenu>
              <DropdownMenuTrigger className="text-foreground hover:text-primary transition-smooth flex items-center gap-1">
                Resources <ChevronDown size={16} aria-hidden />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {RESOURCES_WEBCENTER.map((l) => (
                  <DropdownMenuItem asChild key={l.to}>
                    <Link to={l.to}>{l.label}</Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                {RESOURCES_COMPANY.map((l) => (
                  <DropdownMenuItem asChild key={l.to}>
                    <Link to={l.to}>{l.label}</Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <a href="https://docs.ezcloud.ai" target="_blank" rel="noopener noreferrer">
                    Docs
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              onClick={() => handleSectionNavigation("contact")}
              className="text-foreground hover:text-primary transition-smooth"
              aria-label="Navigate to Contact section"
            >
              Contact
            </button>

            <Button
              variant="accent"
              size="sm"
              onClick={() => openCalendlyDemo()}
            >
              Schedule a Demo
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border max-h-[80vh] overflow-y-auto">
            <div className="flex flex-col space-y-6">
              <section>
                <div className="text-foreground font-semibold mb-2">Platform</div>
                <div className="flex flex-col pl-4 space-y-2">
                  {PLATFORM_LINKS.map((l) => (
                    <Link key={l.to} to={l.to} onClick={() => setIsMenuOpen(false)} className="text-foreground hover:text-primary transition-smooth py-1">
                      {l.label}
                    </Link>
                  ))}
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleSectionNavigation("how-it-works");
                    }}
                    className="text-foreground hover:text-primary transition-smooth text-left py-1"
                  >
                    How it works
                  </button>
                </div>
              </section>

              <section>
                <div className="text-foreground font-semibold mb-2">Solutions</div>
                <div className="flex flex-col pl-4 space-y-2">
                  {[...SOLUTIONS_WEBCENTER, ...SOLUTIONS_INDUSTRY].map((l) => (
                    <Link key={l.to} to={l.to} onClick={() => setIsMenuOpen(false)} className="text-foreground hover:text-primary transition-smooth py-1">
                      {l.label}
                    </Link>
                  ))}
                </div>
              </section>

              <section>
                <div className="text-foreground font-semibold mb-2">Industries</div>
                <div className="flex flex-col pl-4 space-y-2">
                  {INDUSTRY_LINKS.map((l) => (
                    <Link key={l.to} to={l.to} onClick={() => setIsMenuOpen(false)} className="text-foreground hover:text-primary transition-smooth py-1">
                      {l.label}
                    </Link>
                  ))}
                </div>
              </section>

              <section>
                <div className="text-foreground font-semibold mb-2">Integrations</div>
                <div className="flex flex-col pl-4 space-y-2">
                  {INTEGRATION_LINKS.map((l) => (
                    <Link key={l.to} to={l.to} onClick={() => setIsMenuOpen(false)} className="text-foreground hover:text-primary transition-smooth py-1">
                      {l.label}
                    </Link>
                  ))}
                  <Link
                    to="/integrations"
                    onClick={() => setIsMenuOpen(false)}
                    className="text-foreground hover:text-primary transition-smooth py-1 font-medium"
                  >
                    See all →
                  </Link>
                </div>
              </section>

              <section>
                <div className="text-foreground font-semibold mb-2">Resources</div>
                <div className="flex flex-col pl-4 space-y-2">
                  {[...RESOURCES_WEBCENTER, ...RESOURCES_COMPANY].map((l) => (
                    <Link key={l.to} to={l.to} onClick={() => setIsMenuOpen(false)} className="text-foreground hover:text-primary transition-smooth py-1">
                      {l.label}
                    </Link>
                  ))}
                  <a
                    href="https://docs.ezcloud.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-foreground hover:text-primary transition-smooth py-1"
                  >
                    Docs
                  </a>
                </div>
              </section>

              <div className="flex flex-col space-y-2 pt-2 border-t border-border">
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    handleSectionNavigation("contact");
                  }}
                  className="text-foreground hover:text-primary transition-smooth text-left py-2"
                >
                  Contact
                </button>
                <Button
                  variant="accent"
                  size="sm"
                  onClick={() => openCalendlyDemo()}
                >
                  Schedule a Demo
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
